import { motion } from "framer-motion";
import { Microscope, ShieldCheck, Snowflake, FileCheck2 } from "lucide-react";

const MICROSCOPE =
  "https://images.unsplash.com/photo-1630959305606-3123a081dada?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwxfHxzY2llbmNlJTIwbWljcm9zY29wZSUyMGRhcmslMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc5MDQ5MzAzM3ww&ixlib=rb-4.1.0&q=85";

const EASE = [0.16, 1, 0.3, 1];

const PILLARS = [
  { icon: Microscope, title: "HPLC Verified", text: "Purity confirmed by high-performance liquid chromatography on every synthesis batch." },
  { icon: ShieldCheck, title: "Mass-Spec Confirmed", text: "Molecular identity validated against reference standards before release." },
  { icon: Snowflake, title: "Cold-Chain Shipped", text: "Lyophilized and dispatched in temperature-controlled packaging." },
  { icon: FileCheck2, title: "Batch COA", text: "Full certificate of analysis, endotoxin screen and lot traceability included." },
];

export const Quality = () => (
  <section id="quality" data-testid="quality-section" className="relative py-28 md:py-40 border-t border-[#E4E4E7] bg-[#F4F4F5]">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.9, ease: EASE }}
      >
        <p className="font-mono-clinical text-xs tracking-[0.3em] uppercase text-[#6D28D9] mb-4">02 — Standard</p>
        <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight uppercase leading-[1.05]">
          Analytical rigor,
          <br />
          by design.
        </h2>
        <p className="text-[#71717A] text-base md:text-lg mt-6 max-w-lg leading-relaxed">
          We treat every microgram like it matters — because in research, it does. Synthesis, purification and
          lyophilization run under one documented chain of custody.
        </p>
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-px bg-[#E4E4E7] border border-[#E4E4E7]">
          {PILLARS.map((p, i) => (
            <motion.div
              key={p.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: i * 0.08, ease: EASE }}
              data-testid={`quality-pillar-${i}`}
              className="bg-[#FFFFFF] p-6"
            >
              <p.icon className="w-5 h-5 text-[#6D28D9] mb-4" strokeWidth={1.5} />
              <h3 className="font-display font-bold text-[#09090B] text-sm uppercase tracking-wide">{p.title}</h3>
              <p className="text-sm text-[#71717A] mt-2 leading-relaxed">{p.text}</p>
            </motion.div>
          ))}
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 1.1, ease: EASE }}
        className="relative"
      >
        <div className="absolute -top-4 -left-4 w-full h-full border border-[#6D28D9]/30 pointer-events-none" />
        <div className="relative overflow-hidden">
          <img src={MICROSCOPE} alt="Researcher at microscope in dark laboratory" className="w-full h-[520px] object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#08080A]/80 via-transparent to-transparent" />
          <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between">
            <p className="font-mono-clinical text-[10px] tracking-[0.25em] uppercase text-white/70">
              Lab 04 — Analytical Suite
            </p>
            <p className="font-display text-5xl font-black text-white drop-shadow-[0_0_15px_rgba(109,40,217,0.25)]">
              99%<span className="text-[#6D28D9] text-2xl align-top">+</span>
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  </section>
);
